import React, { useState } from "react";
import { formatCompactCurrency } from "@/utils/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Shield, Heart, IndianRupee, AlertTriangle, CheckCircle, TrendingUp } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useLifeInsuranceCalculator, useHealthInsuranceCalculator } from "@/hooks/useCalculatorApi";

interface LifeInsuranceInputs {
  currentAge: number;
  annualIncome: number;
  currentExpenses: number;
  outstandingLoans: number;
  dependents: number;
  existingInsurance: number;
}

interface HealthInsuranceInputs {
  age: number;
  familySize: number;
  cityTier: number;
  medicalHistory: string;
  existingCoverage: number;
}

/**
 * Insurance Calculator component for life and health insurance needs analysis.
 * Calculates optimal coverage amounts using multiple methodologies.
 * @example
 * <InsuranceCalculator />
 * @returns {JSX.Element} Complete insurance planning interface with dual calculators.
 */
const InsuranceCalculator: React.FC = () => {
  const [lifeInputs, setLifeInputs] = useState<LifeInsuranceInputs>({
    currentAge: 30,
    annualIncome: 1200000,
    currentExpenses: 600000,
    outstandingLoans: 0,
    dependents: 2,
    existingInsurance: 0,
  });

  const [healthInputs, setHealthInputs] = useState<HealthInsuranceInputs>({
    age: 30,
    familySize: 3,
    cityTier: 1,
    medicalHistory: "none",
    existingCoverage: 0,
  });

  interface InsuranceRecommendation {
    type: "tip" | "warning" | "info";
    title: string;
    description: string;
  }

  const mapRecommendationType = (type: string): InsuranceRecommendation["type"] => {
    if (type === "critical") {
      return "warning";
    }
    if (type === "suggestion" || type === "tip") {
      return "tip";
    }
    return "info";
  };

  interface InsuranceResult {
    summary: {
      recommendedCoverage?: number;
      estimatedPremium?: number;
      [key: string]: unknown;
    };
    breakdown: {
      [key: string]: unknown;
    };
    recommendations: InsuranceRecommendation[];
  }

  const [lifeResult, setLifeResult] = useState<InsuranceResult | null>(null);
  const [healthResult, setHealthResult] = useState<InsuranceResult | null>(null);
  const { loading: lifeLoading, calculateLifeInsurance } = useLifeInsuranceCalculator();
  const { loading: healthLoading, calculateHealthInsurance } = useHealthInsuranceCalculator();

  const handleLifeCalculation = async () => {
    const data = await calculateLifeInsurance(lifeInputs);
    if (data?.summary && data?.breakdown) {
      setLifeResult({
        summary: data.summary,
        breakdown: data.breakdown,
        recommendations: Array.isArray(data.recommendations)
          ? data.recommendations.map((item) => ({
              type: mapRecommendationType(item.type),
              title: item.title,
              description: item.description,
            }))
          : [],
      });
    }
  };

  const handleHealthCalculation = async () => {
    const data = await calculateHealthInsurance(healthInputs);
    if (data?.summary && data?.breakdown) {
      setHealthResult({
        summary: data.summary,
        breakdown: data.breakdown,
        recommendations: Array.isArray(data.recommendations)
          ? data.recommendations.map((item) => ({
              type: mapRecommendationType(item.type),
              title: item.title,
              description: item.description,
            }))
          : [],
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-bold">Insurance Calculator</h2>
        </div>
        <p className="text-muted-foreground">
          Calculate your life and health insurance coverage needs
        </p>
      </div>

      <Tabs defaultValue="life" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="life" className="gap-2">
            <Shield className="h-4 w-4" />
            Life Insurance
          </TabsTrigger>
          <TabsTrigger value="health" className="gap-2">
            <Heart className="h-4 w-4" />
            Health Insurance
          </TabsTrigger>
        </TabsList>

        {/* Life Insurance Tab */}
        <TabsContent value="life" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Life Insurance Input */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Life Insurance Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Current Age</Label>
                  <Input
                    type="number"
                    value={lifeInputs.currentAge}
                    onChange={(e) =>
                      setLifeInputs((prev) => ({
                        ...prev,
                        currentAge: Number.parseInt(e.target.value, 10) || 0,
                      }))
                    }
                    min={18}
                    max={65}
                  />
                </div>

                <div>
                  <Label>Annual Income</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-10"
                      value={lifeInputs.annualIncome}
                      onChange={(e) =>
                        setLifeInputs((prev) => ({
                          ...prev,
                          annualIncome: Number.parseInt(e.target.value, 10) || 0,
                        }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <Label>Annual Expenses</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-10"
                      value={lifeInputs.currentExpenses}
                      onChange={(e) =>
                        setLifeInputs((prev) => ({
                          ...prev,
                          currentExpenses: Number.parseInt(e.target.value, 10) || 0,
                        }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <Label>Outstanding Loans</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-10"
                      value={lifeInputs.outstandingLoans}
                      onChange={(e) =>
                        setLifeInputs((prev) => ({
                          ...prev,
                          outstandingLoans: Number.parseInt(e.target.value, 10) || 0,
                        }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <Label>Number of Dependents</Label>
                  <Input
                    type="number"
                    value={lifeInputs.dependents}
                    onChange={(e) =>
                      setLifeInputs((prev) => ({
                        ...prev,
                        dependents: Number.parseInt(e.target.value, 10) || 0,
                      }))
                    }
                    min={0}
                    max={10}
                  />
                </div>

                <div>
                  <Label>Existing Life Insurance</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-10"
                      value={lifeInputs.existingInsurance}
                      onChange={(e) =>
                        setLifeInputs((prev) => ({
                          ...prev,
                          existingInsurance: Number.parseInt(e.target.value, 10) || 0,
                        }))
                      }
                    />
                  </div>
                </div>

                <Button onClick={handleLifeCalculation} className="w-full" disabled={lifeLoading}>
                  {lifeLoading ? "Calculating..." : "Calculate Life Insurance Need"}
                </Button>
              </CardContent>
            </Card>

            {/* Life Insurance Results */}
            {lifeResult && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Life Insurance Analysis
                    <Badge
                      variant={
                        Number(lifeResult.summary.adequacyRatio) >= 80 ? "default" : "secondary"
                      }
                      className="ml-auto"
                    >
                      {lifeResult.summary.adequacyRatio}% Covered
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">Recommended Coverage</p>
                      <p className="text-lg font-bold text-primary">
                        {formatCompactCurrency(lifeResult.summary.recommendedCoverage)}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">Current Coverage</p>
                      <p className="text-lg font-bold">
                        {formatCompactCurrency(lifeResult.summary.existingCoverage)}
                      </p>
                    </div>
                  </div>

                  {lifeResult.summary.coverageGap > 0 && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-1">
                          <p className="font-semibold">Coverage Gap Identified</p>
                          <p className="text-sm">
                            You need additional coverage of ₹
                            {formatCompactCurrency(lifeResult.summary.coverageGap)}
                          </p>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <h4 className="font-semibold">Coverage Breakdown</h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>Income Replacement:</span>
                        <span>{formatCompactCurrency(lifeResult.breakdown.incomeReplacement)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Outstanding Loans:</span>
                        <span>{formatCompactCurrency(lifeResult.breakdown.outstandingLoans)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Child Education:</span>
                        <span>{formatCompactCurrency(lifeResult.breakdown.childEducation)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Emergency Fund:</span>
                        <span>{formatCompactCurrency(lifeResult.breakdown.emergencyFund)}</span>
                      </div>
                    </div>
                  </div>

                  {lifeResult.recommendations && (
                    <div className="space-y-2">
                      {lifeResult.recommendations.map((rec: InsuranceRecommendation) => (
                        <Alert key={rec.title}>
                          <CheckCircle className="h-4 w-4" />
                          <AlertDescription>
                            <div className="space-y-1">
                              <p className="font-semibold">{rec.title}</p>
                              <p className="text-sm">{rec.description}</p>
                            </div>
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Health Insurance Tab */}
        <TabsContent value="health" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Health Insurance Input */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5" />
                  Health Insurance Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Age</Label>
                  <Input
                    type="number"
                    value={healthInputs.age}
                    onChange={(e) =>
                      setHealthInputs((prev) => ({
                        ...prev,
                        age: Number.parseInt(e.target.value, 10) || 0,
                      }))
                    }
                    min={18}
                    max={80}
                  />
                </div>

                <div>
                  <Label>Family Size</Label>
                  <Input
                    type="number"
                    value={healthInputs.familySize}
                    onChange={(e) =>
                      setHealthInputs((prev) => ({
                        ...prev,
                        familySize: Number.parseInt(e.target.value, 10) || 0,
                      }))
                    }
                    min={1}
                    max={10}
                  />
                </div>

                <div>
                  <Label>City Tier</Label>
                  <Select
                    value={healthInputs.cityTier.toString()}
                    onValueChange={(value) =>
                      setHealthInputs((prev) => ({
                        ...prev,
                        cityTier: Number.parseInt(value, 10),
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Metro Cities (Mumbai, Delhi, Bangalore)</SelectItem>
                      <SelectItem value="2">Tier-1 Cities (Pune, Chennai, Hyderabad)</SelectItem>
                      <SelectItem value="3">Tier-2 Cities & Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Medical History</Label>
                  <Select
                    value={healthInputs.medicalHistory}
                    onValueChange={(value) =>
                      setHealthInputs((prev) => ({
                        ...prev,
                        medicalHistory: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Medical History</SelectItem>
                      <SelectItem value="minor">Minor Conditions</SelectItem>
                      <SelectItem value="major">Major Medical Conditions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Existing Health Coverage</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-10"
                      value={healthInputs.existingCoverage}
                      onChange={(e) =>
                        setHealthInputs((prev) => ({
                          ...prev,
                          existingCoverage: Number.parseInt(e.target.value, 10) || 0,
                        }))
                      }
                    />
                  </div>
                </div>

                <Button
                  onClick={handleHealthCalculation}
                  className="w-full"
                  disabled={healthLoading}
                >
                  {healthLoading ? "Calculating..." : "Calculate Health Insurance Need"}
                </Button>
              </CardContent>
            </Card>

            {/* Health Insurance Results */}
            {healthResult && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="h-5 w-5" />
                    Health Insurance Analysis
                    <Badge
                      variant={
                        Number(healthResult.summary.adequacyRatio) >= 80 ? "default" : "secondary"
                      }
                      className="ml-auto"
                    >
                      {healthResult.summary.adequacyRatio}% Covered
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">Recommended Coverage</p>
                      <p className="text-lg font-bold text-primary">
                        {formatCompactCurrency(healthResult.summary.recommendedCoverage)}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">Current Coverage</p>
                      <p className="text-lg font-bold">
                        {formatCompactCurrency(healthResult.summary.existingCoverage)}
                      </p>
                    </div>
                  </div>

                  {healthResult.summary.coverageGap > 0 && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-1">
                          <p className="font-semibold">Coverage Gap Identified</p>
                          <p className="text-sm">
                            You need additional coverage of ₹
                            {formatCompactCurrency(healthResult.summary.coverageGap)}
                          </p>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <h4 className="font-semibold">Coverage Calculation</h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>Base Coverage:</span>
                        <span>{formatCompactCurrency(healthResult.breakdown.baseCoverage)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Family Multiplier:</span>
                        <span>{healthResult.breakdown.familyMultiplier}x</span>
                      </div>
                      <div className="flex justify-between">
                        <span>City Adjustment:</span>
                        <span>{healthResult.breakdown.cityMultiplier}x</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Medical History:</span>
                        <span>{healthResult.breakdown.medicalMultiplier}x</span>
                      </div>
                    </div>
                  </div>

                  {healthResult.recommendations && (
                    <div className="space-y-2">
                      {healthResult.recommendations.map((rec: InsuranceRecommendation) => (
                        <Alert key={rec.title}>
                          <CheckCircle className="h-4 w-4" />
                          <AlertDescription>
                            <div className="space-y-1">
                              <p className="font-semibold">{rec.title}</p>
                              <p className="text-sm">{rec.description}</p>
                            </div>
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InsuranceCalculator;
